import template from './documentTemplate.json';
import { DOORLOPENDE_REIS_DEFAULTS, DOORLOPENDE_REIS_MVZBMIL } from './fields';
import { getProductEntry } from '../../../products/registry';
import { computeDoorlopendeReisDisplayPremium } from '../../../utils/doorlopendeReisPricing';

// Product wiring (carrier, product name, calc endpoint) comes from the central registry.
const DFD = getProductEntry('doorlopendereis')!;

// Same calc mechanism as the other product screens: POST the document to
// productservice/calculate wrapped with carrier + product name.
export const DFD_CALC_ENDPOINT = DFD.calcEndpoint;

export interface VpAddress {
  VP_PCODE?: string;
  VP_HUISNR?: string | number;
  VP_TOEVOEG?: string;
  VP_STRAAT?: string;
  VP_PLAATS?: string;
}

export interface DfdPremiums {
  display: number | null;
  OD_NTP: number | null;
  PP_NTP: number | null;
  PP_TTOT: number | null;
}

const clone = <T>(o: T): T => JSON.parse(JSON.stringify(o));

const isEmptyObject = (x: any) =>
  x !== null && typeof x === 'object' && !Array.isArray(x) && Object.keys(x).length === 0;

// Make the payload match the lean shape the working products (e.g. rhion AVP2025) send:
//  - drop properties that are "" or null (so numeric/date fields are never sent as "",
//    which is what crashes the .NET parser with "input string '' was not in a correct format");
//  - collapse repeating groups (CL/VW/BY/…) to [] by dropping placeholder rows that
//    sanitize to empty objects (empty arrays themselves are kept);
//  - omit objects that end up with no real data (PK, RC, OD.MP, …).
// Numbers, booleans, 0, non-empty strings and populated objects are left untouched.
export const sanitizeDocument = (v: any): any => {
  if (Array.isArray(v)) {
    return v.map(sanitizeDocument).filter((x) => x != null && x !== '' && !isEmptyObject(x));
  }
  if (v && typeof v === 'object') {
    const out: any = {};
    for (const k of Object.keys(v)) {
      const sv = sanitizeDocument(v[k]);
      if (sv === '' || sv == null || isEmptyObject(sv)) continue;
      out[k] = sv;
    }
    return out;
  }
  return v;
};

const pad = (n: number) => String(n).padStart(2, '0');
const n8 = (d: Date) => Number(`${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`);

// Route a flat `values` entry (keyed by AFD field code) into its branch of the
// Contractdocument, matching the empty.json template shape.
const routeValue = (doc: any, key: string, value: string | number) => {
  const pp = doc.Contractdocument.PP;
  if (key.startsWith('OD_')) pp.OD[key] = value;
  else if (key.startsWith('VZ_')) pp.VZ[key] = value;
  else if (key.startsWith('VE_')) pp.VE[key] = value;
  else if (key.startsWith('VP_')) pp.VP[key] = value;
  else if (key.startsWith('PP_')) pp[key] = value;
  // Ongevallen / other Supportsection flags are UI-only and not sent to the BFF.
};

/** Build the Contractdocument the BFF expects from the flat screen values + address. */
export const buildDfdDocument = (
  values: Record<string, string | number>,
  vp?: VpAddress | null,
  appName?: string,
): any => {
  const doc: any = clone(template);

  // Guarantee the system/default fields (PP_BETTERM, PP_STPRV, VE_MYAAND, …) are always
  // in the payload — the BFF marks them verplicht. Merging here makes the request
  // self-sufficient regardless of whether the screen has persisted the defaults into
  // step data yet (avoids "… ontbreekt" on first calc).
  const vals = { ...DOORLOPENDE_REIS_DEFAULTS, ...values };
  Object.entries(vals).forEach(([k, v]) => routeValue(doc, k, v));

  // The address lives in Contractdocument.PP.VP (the BFF only wants the Contractdocument).
  const ppVp = doc.Contractdocument.PP.VP;
  if (vp?.VP_PCODE) {
    ppVp.VP_PCODE = vp.VP_PCODE;
    ppVp.VP_STRAAT = vp.VP_STRAAT ?? '';
    ppVp.VP_PLAATS = vp.VP_PLAATS ?? '';
    ppVp.VP_TOEVOEG = vp.VP_TOEVOEG ?? '';
    const hn = vp.VP_HUISNR;
    ppVp.VP_HUISNR = hn != null && String(hn).trim() !== '' && !isNaN(Number(hn)) ? Number(hn) : hn ?? '';
  }

  // Required dates (N8), set programmatically like the other screens: ingang = today,
  // hoofdvervaldatum = 1st of the same month next year.
  const now = new Date();
  const ingang = n8(now);
  doc.Contractdocument.PP.PP_INGDAT = ingang;
  doc.Contractdocument.PP.PP_INGDTW = ingang;
  doc.Contractdocument.PP.PP_HVVDAT = Number(`${now.getFullYear() + 1}${pad(now.getMonth() + 1)}01`);

  doc.Contractdocument.PP.PP_PRODNM = DFD.productName;
  doc.Contractdocument.PP.PP_PRODUCT = DFD.productName;

  // DR is the only coverage this product calculates. Per the engine team, the Ongevallen
  // (OD) cover is folded implicitly into DR, so the separate OD object is not sent. The
  // beroepsmilitair question was dropped — DR_MVZBMIL is always 'N' (see below).
  delete doc.Contractdocument.PP.OD;
  delete doc.Contractdocument.PP.VZ;

  // The live K781 calc prices the travel coverage from PP.DR (the BFF returns
  // "Verplicht element PP/DR/… ontbreekt" without it). DR_CODE=3001 is confirmed by the
  // backend; DR_GEBIED='W' is still a guess pending the real codelist. DR_GSS must come
  // from a selected Gezinssamenstelling or it sanitizes out ("DR_GSS ontbreekt"). The
  // OD_* inputs (gezinssamenstelling, invaliditeit/overlijden sums, combinatie) map in here.
  doc.Contractdocument.PP.DR = {
    DR_CODE: '3001',
    DR_GSS: vals.OD_GSS ?? '',
    DR_GEBIED: (vals.OD_GEBIED as string) ?? 'W',
    DR_MVZBMIL: DOORLOPENDE_REIS_MVZBMIL,
    DR_COMBINA: vals.OD_COMBINA ?? '',
    DR_PPRC: vals.OD_PPRC ?? 20,
  };

  // AL app metadata — sent on every product, same as the other screens.
  doc.Contractdocument.AL.AL_APPLNM = appName || 'SAA Verzekeringen';
  doc.Contractdocument.AL.AL_URLNAAM = (typeof window !== 'undefined' ? window.location.origin : '') + '/';

  // XG is a BFF *response* container (status/messages) — never send it in.
  delete doc.Contractdocument.XG;

  // Send only the Contractdocument — Relatiedocument / Supportsection are not passed.
  return { Contractdocument: sanitizeDocument(doc.Contractdocument) };
};

export const restoreDoorlopendeReisInputs = (dr: any): any => ({
  ...(dr || {}),
  DR_MVZBMIL: DOORLOPENDE_REIS_MVZBMIL,
});

export const buildDfdRequest = (
  values: Record<string, string | number>,
  vp?: VpAddress | null,
  appName?: string,
) => ({
  carrierCode: DFD.carrierCode,
  productName: DFD.productName,
  input: buildDfdDocument(values, vp, appName),
});

const num = (v: any): number | null => (typeof v === 'number' ? v : v != null && v !== '' ? Number(v) : null);

/** Pull the display premiums out of a calc response. */
export const extractPremiums = (response: any): DfdPremiums => {
  const pp = response?.output?.Contractdocument?.PP;
  return {
    display: computeDoorlopendeReisDisplayPremium(pp),
    OD_NTP: num(pp?.OD?.OD_NTP),
    PP_NTP: num(pp?.PP_NTP),
    PP_TTOT: num(pp?.PP_TTOT),
  };
};

export const extractEngineMessages = (response: any): string[] => {
  const xm = response?.output?.Contractdocument?.XG?.XM;
  return (Array.isArray(xm) ? xm : xm ? [xm] : [])
    .map((m: any) => m?.XM_STATTXT)
    .filter(Boolean);
};
